import React, { useEffect, useState } from "react";
import { api } from "../../api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

// Turn subscribers into a CSV file the user can download and import into
// their email tool (Mailchimp, Buttondown, Sendinblue, plain Gmail, etc.).
const downloadCsv = (rows) => {
  const header = ["email", "name", "source", "created_at"];
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header.join(",")]
    .concat(rows.map((r) => header.map((h) => escape(r[h])).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const SubscribersInbox = () => {
  const [subs, setSubs] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.listSubscribers().then(setSubs).catch(() => setSubs([]));

  useEffect(() => { load(); }, []);

  const onDelete = async (s) => {
    if (!confirm(`Remove ${s.email} from the list?`)) return;
    try {
      await api.deleteSubscriber(s.id);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
    } catch {
      alert("Delete failed");
    }
  };

  const copyEmails = async () => {
    const text = subs.map((s) => s.email).join(", ");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      alert("Copy failed — try selecting manually.");
    }
  };

  if (subs === null) return <p className="p-8 font-serif text-[color:var(--ink-mute)]">Loading…</p>;

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <p className="font-serif text-2xl">Newsletter subscribers</p>
          <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mt-1">
            {subs.length} email{subs.length !== 1 ? "s" : ""} collected.
            {subs.length > 0 && " Export to CSV or copy all addresses when you're ready to send a campaign."}
          </p>
        </div>
        {subs.length > 0 && (
          <div className="flex items-center gap-4">
            <button
              onClick={copyEmails}
              className="font-serif text-[13px] tlink"
              data-testid="subs-copy-emails"
            >
              {copied ? "Copied ✓" : "Copy all emails"}
            </button>
            <button
              onClick={() => downloadCsv(subs)}
              className="btn-sober"
              data-testid="subs-download-csv"
            >
              Download CSV
            </button>
          </div>
        )}
      </div>

      {subs.length === 0 ? (
        <p className="font-serif text-[14px] text-[color:var(--ink-mute)] italic">
          No subscribers yet. Share the Contact page — the newsletter form is at the bottom.
        </p>
      ) : (
        <table className="w-full text-left font-serif text-[14px]" data-testid="subs-table">
          <thead>
            <tr className="border-b border-[color:var(--line)] text-[color:var(--ink-mute)]">
              <th className="py-2 pr-3 font-normal">Email</th>
              <th className="py-2 pr-3 font-normal">Name</th>
              <th className="py-2 pr-3 font-normal">Source</th>
              <th className="py-2 pr-3 font-normal">Joined</th>
              <th className="py-2 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} className="border-b border-[color:var(--line)]/60 group">
                <td className="py-2 pr-3">
                  <a href={`mailto:${s.email}`} className="tlink">{s.email}</a>
                </td>
                <td className="py-2 pr-3 text-[color:var(--ink-mute)]">{s.name || "—"}</td>
                <td className="py-2 pr-3 text-[color:var(--ink-mute)]">{s.source}</td>
                <td className="py-2 pr-3 text-[color:var(--ink-mute)] counter text-[13px]">{formatDate(s.created_at)}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => onDelete(s)}
                    className="text-[13px] text-red-600 tlink opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`sub-delete-${s.id}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SubscribersInbox;
